import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '../../lib/errors'
import { formatDateTime } from '../../lib/format'
import type { TableColumn } from '../../lib/tableView'
import { useTableView } from '../../lib/useTableView'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import { verifyBroker, verifyCarrier, type FmcsaCandidate, type FmcsaVerification } from './api'

/** A candidate's place, as the table says it. */
function candidatePlace(candidate: FmcsaCandidate): string | null {
  return candidate.city === null && candidate.state === null
    ? null
    : `${candidate.city ?? ''}${candidate.city && candidate.state ? ', ' : ''}${candidate.state ?? ''}`
}

/** What each column of the register's candidates sorts and filters by (TRKC-110). */
const CANDIDATE_COLUMNS: readonly TableColumn<FmcsaCandidate>[] = [
  { key: 'name', sortValue: (candidate) => candidate.legalName, filter: { kind: 'text', text: (candidate) => candidate.legalName } },
  { key: 'usdot', sortValue: (candidate) => candidate.usdotNumber, filter: { kind: 'text', text: (candidate) => candidate.usdotNumber } },
  { key: 'location', sortValue: candidatePlace, filter: { kind: 'text', text: candidatePlace } },
]

/** The heading each column's filter box is named after. */
const CANDIDATE_HEADINGS: Record<string, string> = {
  name: 'fmcsa.legalName',
  usdot: 'fmcsa.usdotNumber',
  location: 'fmcsa.location',
}

/**
 * The companies a docket number names, when it names more than one (1.3): sorted and narrowed
 * by their columns, as every table is (TRKC-110).
 */
function Candidates({ candidates }: { candidates: FmcsaCandidate[] }) {
  const { t } = useTranslation()
  const view = useTableView(candidates, CANDIDATE_COLUMNS)

  return (
    <>
      <TableScroll label={t('fmcsa.candidates')}>
        <table className="table">
          <caption className="visually-hidden">{t('fmcsa.candidates')}</caption>
          <thead>
            <tr>
              {CANDIDATE_COLUMNS.map((column) => (
                <SortableHeader key={column.key} view={view} column={column.key}>
                  {t(CANDIDATE_HEADINGS[column.key] ?? column.key)}
                </SortableHeader>
              ))}
            </tr>
            <tr className="table__filters">
              {CANDIDATE_COLUMNS.map((column) => (
                <FilterCell key={column.key} view={view} column={column} label={t(CANDIDATE_HEADINGS[column.key] ?? column.key)} />
              ))}
            </tr>
          </thead>
          <tbody>
            {view.rows.length === 0 && (
              <tr>
                <td colSpan={CANDIDATE_COLUMNS.length} className="placeholder">
                  {t('table.noneMatch')}
                </td>
              </tr>
            )}
            {view.rows.map((candidate) => (
              <tr key={candidate.usdotNumber ?? candidate.legalName}>
                <td>{candidate.legalName ?? '—'}</td>
                <td className="table__numeric">{candidate.usdotNumber ?? '—'}</td>
                <td>{candidatePlace(candidate) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      <FilterSummary view={view} />
    </>
  )
}

/**
 * "Verify with FMCSA" and whatever the register answers.
 *
 * The register is evidence, never a gate: a name that does not match its legal name
 * is shown as a warning and nothing more, because FMCSA lags reality by weeks and
 * nobody writes "COYOTE LOGISTICS LLC" on a rate confirmation. The four outcomes are
 * rendered as four different things on purpose — an unreachable register must never
 * look like a carrier that does not exist.
 */
export function FmcsaVerificationPanel({
  ownerType,
  ownerId,
  storedVerification = null,
  hasUnsavedChanges = false,
  hasIdentifiers = false,
  askOnMount = false,
}: {
  ownerType: 'Broker' | 'Carrier'
  ownerId: string
  /** What the register said last time, carried by the record itself. */
  storedVerification?: FmcsaVerification | null
  /**
   * Whether the screen around the panel holds edits nobody has saved. The register
   * is asked about the RECORD, so verifying a draft would answer about the old
   * numbers and print the answer beside the new ones.
   */
  hasUnsavedChanges?: boolean
  /** Whether the saved record carries a USDOT or MC number at all — without one there is nothing to ask. */
  hasIdentifiers?: boolean
  /**
   * Whether the screen around the panel was reached by saving the record — the one
   * moment the register is asked by itself, because it is the moment Hugo expected
   * the confirmation (TRKC-79). Never on an ordinary visit: only a Verified answer is
   * filed, so a record whose last ask was NotFound or Unavailable looks never-asked,
   * and asking on every visit would be a toll paid hardest during an outage.
   */
  askOnMount?: boolean
}) {
  const { t, i18n } = useTranslation()

  // Two separate things, deliberately. `filed` is the evidence the record holds —
  // it survives leaving the page, and only a successful verification replaces it.
  // `attempt` is how the last ask went, which may be "the register is down" — and
  // an outage must never erase evidence it had no part in producing.
  const [filed, setFiled] = useState<FmcsaVerification | null>(storedVerification)

  // Re-synced whenever the record around the panel is read again with different
  // evidence — the useState-from-a-prop trap this repo has paid for before. A parent
  // that re-reads the record after a document or activation change brings the
  // record's truth, and it replaces whatever this mount had on screen.
  useEffect(() => {
    setFiled(storedVerification)
  }, [storedVerification])
  const [attempt, setAttempt] = useState<FmcsaVerification | null>(null)
  const [failure, setFailure] = useState<{ error: unknown } | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)

  async function handleVerify() {
    setIsVerifying(true)
    setFailure(null)
    setAttempt(null)

    try {
      const answer = await (ownerType === 'Broker' ? verifyBroker(ownerId) : verifyCarrier(ownerId))

      // Only a verified answer becomes the record's evidence — that is exactly
      // what the server files, and the screen must not claim more than the record.
      if (answer.outcome === 'Verified' && answer.filed) {
        setFiled(answer)
      } else {
        // Includes a verified answer the server could not file: it is the story of
        // this attempt, never the record's evidence.
        setAttempt(answer)
      }
    } catch (caught) {
      // A failed request changed nothing in storage, so the evidence stays and the
      // failure is reported beside it.
      setFailure({ error: caught })
    } finally {
      setIsVerifying(false)
    }
  }

  // Asked once, by itself, when the record was just saved with numbers and holds no
  // answer. Four things keep this from becoming "on every page load", which 1.3
  // refused: only a save opens the door, an answer already on file is never re-asked
  // (asking again is a choice), a record with nothing to ask about is left alone,
  // and unsaved edits still hold it shut.
  const askedOnMount = useRef(false)
  useEffect(() => {
    if (
      !askOnMount ||
      askedOnMount.current ||
      storedVerification !== null ||
      !hasIdentifiers ||
      hasUnsavedChanges
    ) {
      return
    }

    askedOnMount.current = true
    void handleVerify()
    // The ask is keyed on the record's state, not on the handler, which only ever
    // reads props that cannot change without remounting the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askOnMount, storedVerification, hasIdentifiers, hasUnsavedChanges])

  return (
    <section className="fleet fmcsa">
      <header className="page__header--row fleet__header">
        <h2>{t('fmcsa.title')}</h2>
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void handleVerify()}
          disabled={isVerifying || hasUnsavedChanges}
        >
          {isVerifying ? t('fmcsa.verifying') : t('fmcsa.verify')}
        </button>
      </header>

      <p className="page__subtitle">
        {hasUnsavedChanges ? t('fmcsa.saveFirst') : t('fmcsa.subtitle')}
      </p>

      {failure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(failure.error, t, 'fmcsa.error')}
        </p>
      )}

      {/* How the last ask went, when it produced no new evidence… */}
      {attempt !== null && (
        <Outcome verification={attempt} ownerType={ownerType} language={i18n.language} />
      )}

      {/* …and what the record holds regardless, which an outage cannot erase. */}
      {filed !== null && (
        <Outcome verification={filed} ownerType={ownerType} language={i18n.language} />
      )}
    </section>
  )
}

/** "CHICAGO, IL", or whichever half the register gave, or a dash for neither. */
function place(city: string | null, state: string | null): string {
  if (city === null && state === null) {
    return '—'
  }

  return `${city ?? ''}${city && state ? ', ' : ''}${state ?? ''}`
}

function Outcome({
  verification,
  ownerType,
  language,
}: {
  verification: FmcsaVerification
  ownerType: 'Broker' | 'Carrier'
  language: string
}) {
  const { t } = useTranslation()

  if (verification.outcome === 'NoIdentifier') {
    // Deliberately its own message: nothing is down, and telling somebody to wait
    // out an outage when the fix is a field on this very screen wastes their day.
    return <p className="placeholder">{t('fmcsa.noIdentifier')}</p>
  }

  if (verification.outcome === 'NotFound') {
    return <p className="placeholder">{t('fmcsa.notFound')}</p>
  }

  if (verification.outcome === 'Unavailable') {
    // Deliberately not an error style: the register being unreachable says nothing
    // about this broker, and must not read as a verdict against it.
    return <p className="placeholder">{t('fmcsa.unavailable')}</p>
  }

  if (verification.outcome === 'Ambiguous') {
    return (
      <div>
        <p className="alert alert--warning" role="alert">
          {t('fmcsa.ambiguous')}
        </p>
        <Candidates candidates={verification.candidates ?? []} />
      </div>
    )
  }

  return (
    <div className="result">
      {!verification.filed && (
        <p className="alert alert--warning" role="alert">
          {t('fmcsa.notFiled')}
        </p>
      )}

      {verification.mcNumberConflict !== null && (
        <p className="alert alert--warning" role="alert">
          {verification.mcNumberConflict.kind === 'NotListed'
            ? t('fmcsa.mcNotListed')
            : t('fmcsa.numberConflict', {
                legal: verification.mcNumberConflict.legalName ?? '—',
                usdot: verification.mcNumberConflict.usdotNumber ?? '—',
              })}
        </p>
      )}

      {verification.nameMatches === false && (
        <p className="alert alert--warning" role="alert">
          {t('fmcsa.nameMismatch', {
            entered: verification.enteredName,
            legal: verification.legalName,
          })}
        </p>
      )}

      {/* The place on file against the register's (TRKC-79). A look, never a gate:
          the register holds the physical address, and this file may carry the
          billing one. */}
      {verification.addressMatches === false && (
        <p className="alert alert--warning" role="alert">
          {t('fmcsa.addressMismatch', {
            registered: place(verification.physicalCity, verification.physicalState),
            entered: place(verification.enteredCity, verification.enteredState),
          })}
        </p>
      )}

      {/* Whether the register backs this record's ROLE — the one thing the three
          authority lines below show but never concluded. Only when it was reported:
          a USDOT-only answer is silence, and silence is not "does not hold it". */}
      {verification.authorityForRole === 'NotHeld' && (
        <p className="alert alert--warning" role="alert">
          {t(ownerType === 'Broker' ? 'fmcsa.noBrokerAuthority' : 'fmcsa.noCarrierAuthority')}
        </p>
      )}

      <dl className="result__grid">
        <div>
          <dt>{t('fmcsa.legalName')}</dt>
          <dd>{verification.legalName ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('fmcsa.dbaName')}</dt>
          <dd>{verification.dbaName ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('fmcsa.usdotNumber')}</dt>
          <dd className="table__numeric">{verification.usdotNumber ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('fmcsa.allowedToOperate')}</dt>
          <dd>
            {verification.allowedToOperate === null
              ? t('fmcsa.authority.NotReported')
              : t(verification.allowedToOperate ? 'fmcsa.yes' : 'fmcsa.no')}
          </dd>
        </div>
        <div>
          <dt>{t('fmcsa.location')}</dt>
          <dd>{place(verification.physicalCity, verification.physicalState)}</dd>
        </div>
        <div>
          <dt>{t('fmcsa.commonAuthority')}</dt>
          <dd>{t(`fmcsa.authority.${verification.commonAuthority}`)}</dd>
        </div>
        <div>
          <dt>{t('fmcsa.contractAuthority')}</dt>
          <dd>{t(`fmcsa.authority.${verification.contractAuthority}`)}</dd>
        </div>
        <div>
          <dt>{t('fmcsa.brokerAuthority')}</dt>
          <dd>{t(`fmcsa.authority.${verification.brokerAuthority}`)}</dd>
        </div>
        <div>
          <dt>{t('fmcsa.verifiedAt')}</dt>
          <dd>
            {verification.verifiedAt === null
              ? '—'
              : formatDateTime(verification.verifiedAt, language)}
          </dd>
        </div>
      </dl>
    </div>
  )
}
